<template>
  <div
      v-loading="loading"
      class="replacement-reasons flex flex-auto flex-col">
    <div class="title">
      Select replacement reason
    </div>
    <div class="flex">
      <span class="flex-shrink description">
        Please select a reason for product replacement.
      </span>
      <RefreshButton
          class="flex-shrink"
          style="padding: 0; margin-left: 0.5rem"
          :text="false"
          @click="loadReasons" />
    </div>

    <div>
      <label
          v-if="isBaseStationV1"
          style="color: red">For base station replacements, please reference Alarm Desktop for region and version type
      </label>
      <div class="flex flex-row gap-16">
        <div
            class="flex-1 input-label"
            style="margin-top: 48px; margin-bottom: 8px">
          Reason Group
        </div>
      </div>
      <div class="flex flex-row gap-16">
        <div class="select-replacement-reason">
          <el-select
              v-model="selectedReasonGroup"
              class="reason-group-select"
              :placeholder="selectedReasonGroup.toString()"
              popper-class="select-replacement-reason-dropdown"
              @change="sendReasonGroup">
            <el-option
                v-for="group in reasonGroups"
                :key="group.id"
                class="select-replacement-reason-option"
                :label="group.value"
                :value="group.id">
              <span
                  style="float: left; color: #424242"
                  :class="{'highlight-selection': selectedReasonGroup === group.id}">{{ group.value }}</span>
            </el-option>
          </el-select>
        </div>
      </div>
    </div>
    <div>
      <div class="flex flex-row gap-16">
        <div
            class="flex-1 input-label"
            style="margin-top: 40px; margin-bottom: 8px">
          Replacement Reason
        </div>
      </div>
      <div class="flex flex-row gap-16">
        <div class="select-replacement-reason">
          <el-select
              :value="reasonSelectionText"
              :disabled="!isGroupSelected"
              class="reason-group-select"
              placeholder="Select the replacement reason"
              popper-class="select-replacement-reason-dropdown"
              @change="sendReason">
            <el-option
                v-for="reason in reasonSubset"
                :key="reason.id"
                class="select-replacement-reason-option"
                :label="reason.text"
                :value="reason.id">
              <span
                  style="float: left; color: #424242"
                  :class="{'highlight-selection': selectedReplacementReason === reason.id}">{{ reason.value }} - {{ reason.text }} <span
                      v-show="reason.return_label"
                      style="color: #6e6e6e !important;">With Return Label</span>
              </span>
            </el-option>
          </el-select>
        </div>
      </div>
    </div>
  </div>
</template>

 <script>
import axios from "axios";
import { isEmpty } from "lodash";
import { createNamespacedHelpers } from "vuex";
import RefreshButton from "@/containers/Customer/RefreshButton.vue";
const { mapMutations: requestMutations, mapGetters: replacementGetters } =
  createNamespacedHelpers("db/requests");

export default {
  name: "ReplacementReasons",
  components: { RefreshButton },
  props: {
    device: {
      required: true,
      type: Object,
    },
    selectedGroup: {
      type: Number,
      default: -1,
    },
    selectedReason: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      group: 0,
      replacement: 1,
      selectedReplacementReason: -1,
      selectedReasonGroup: -1,
      isGroupSelected: false,
      reasonSubset: [],
      loading: false,
      selectedReasonCode: "",
      selectedReasonReturnLabel: false,
      selectedReasonName: "",
      isSelectedReturnLabel: false,
    };
  },
  computed: {
    ...replacementGetters(["replacementReasons"]),
    reasonGroups() {
      return this.replacementReasons.reasonGroups;
    },
    allReasons() {
      return this.replacementReasons.allReasons;
    },
    shouldLoadReasons() {
      return isEmpty(this.allReasons) || isEmpty(this.reasonGroups);
    },
    deviceKind() {
      return this.device.type || this.device.kind || this.device.profile.kind;
    },
    isBaseStationV1() {
      return this.deviceKind === "base_station_v1";
    },
    reasonSelectionText() {
      if (this.selectedReasonCode === "") return this.selectedReasonName;
      else if (this.selectedReasonReturnLabel)
        return (
          this.selectedReasonCode +
          " - " +
          this.selectedReasonName +
          " (With Return Label)"
        );
      else return this.selectedReasonCode + " - " + this.selectedReasonName;
    },
  },
  mounted() {
    if (this.shouldLoadReasons) {
      this.loadReasons();
    } else {
      this.setDefaultValues();
    }
  },
  methods: {
    ...requestMutations(["storeReplacementReasons"]),
    setDefaultValues() {
      if (this.selectedGroup === -1 && this.selectedReason === -1) {
        this.selectedReasonGroup = "Select the reason group";
        this.selectedReasonName = "Select the replacement reason";
        this.selectedReplacementReason = -1;
        this.selectedReasonCode = "";
        this.selectedReasonReturnLabel = false;
        this.isGroupSelected = false;
      } else {
        this.setReasonGroup(this.selectedGroup);
        this.setReason(this.selectedReason);
      }
    },
    setReason(selectedReason) {
      this.selectedReplacementReason = selectedReason;
      let name = this.reasonSubset.filter(
        (reason) => reason.id === selectedReason
      );
      this.selectedReasonCode = name[0].value.toString();
      this.selectedReasonReturnLabel = name[0].return_label;
      this.selectedReasonName = name[0].text;
      this.isSelectedReturnLabel = name[0].return_label;
    },
    sendReason(selectedReason) {
      this.setReason(selectedReason);
      this.emitSelectedReasons(true);
    },
    setReasonGroup(group) {
      this.selectedReasonGroup = group;
      this.isGroupSelected = true;
      this.reasonSubset = this.getReasonFromGroups(this.selectedReasonGroup);
    },
    sendReasonGroup(group) {
      this.selectedReasonName = "Select the replacement reason";
      this.selectedReplacementReason = -1;
      this.selectedReasonCode = "";
      this.selectedReasonReturnLabel = false;
      this.setReasonGroup(group);
      if (this.reasonSubset.length === 0) {
        this.emitSelectedReasons(true);
      } else {
        this.emitSelectedReasons(false);
      }
    },
    getReasonFromGroups(group) {
      return this.allReasons.filter(
        (reason) =>
          reason.reason_group_id === group &&
          reason.kinds.includes(this.deviceKind)
      );
    },
    emitSelectedReasons(allowNext) {
      this.$emit("selected-group-and-reason", {
        group: this.selectedReasonGroup,
        reason: this.selectedReplacementReason,
        name: this.selectedReasonName,
        label: this.isSelectedReturnLabel,
        allowNext: allowNext,
      });
    },
    loadReasons() {
      this.loading = true;
      axios
        .get("/v2/reasons.json?active=true")
        .then((result) => {
          let groups = result.data.groups;
          let reasonGroups = groups.filter(
            (group) => group.key === "replacement"
          );
          if (this.deviceKind) {
            reasonGroups = reasonGroups.filter(
              (group) =>
                !!result.data.reasons.find(
                  (reason) =>
                    reason.reason_group_id === group.id &&
                    reason.kinds.includes(this.deviceKind)
                )
            );
          }
          this.storeReplacementReasons({
            allReasons: result.data.reasons,
            reasonGroups,
          });
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loading = false;
          this.setDefaultValues();
        });
    },
  },
};
</script>

 <style>
.replacement-reasons {
  gap: 8px;
}
.replacement-reasons .title {
  height: 36px;
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 36px;
  display: flex;
  align-items: flex-end;
  color: #424242;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
}

.replacement-reasons .description {
  height: 24px;
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: #424242;
  margin: 0 !important;
}

.replacement-reasons .reason-group-select {
  display: flex;
  width: 100%;
  height: 48px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: #424242;
}
.select-replacement-reason {
  display: flex;
  align-self: center;
  width: 644px !important;
}
.highlight-selection {
  color: #1998d5 !important;
  font-weight: bold;
}
</style>
